import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import { countriesList, extractTraderCountryCode, getCountryLabelByCode, normalizeCountryCode, traderMatchesCountryFilter } from "../../../data/countriesList";
import {
  ALLOWED_EMAIL_PROVIDERS_LABEL,
  isAllowedEmailDomain,
  isValidEmailFormat,
} from "../../../utils/allowedEmailDomains";
import "../Home/Homepage.css";
import "./TradersListPage.css";
import InternalTransferSettingsPanel from "./InternalTransferSettingsPanel";
import { formatWalletType } from "../../../utils/walletTypes";

const KYC_LABELS = { 0: "Not Verified", 1: "Pending", 2: "Verified", 3: "Verified" };
const STATUS_LABELS = { 0: "Inactive", 1: "Active" };

const FALLBACK_WALLET_TYPES = ["main", "spot", "p2p", "futures", "swap", "earning", "staking"];

const EXPORT_BATCH = 500;
const EXCEL_SHEET_TRADERS = "traders list";
const EXCEL_SHEET_BLOCKED = "blocked traders";
const EXCEL_SHEET_UNVERIFIED = "unverified traders";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso || "—";
  }
};

/** Prefer full email for admin modal; list row may use masked fallback in `emailId`. */
const traderEmailForModal = (u) => {
  if (!u) return "—";
  const v = u.emailPlain ?? u.email ?? u.emailId ?? u.email_masked ?? u.email_id;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "—";
};

const digitsOnly = (v) => String(v ?? "").replace(/\D/g, "");

/** Raw mobile digits for edit form (not masked / not dial-prefixed display). */
const traderMobileForModal = (u) => {
  if (!u) return "";
  if (u.mobilePlain != null && String(u.mobilePlain).trim() !== "") {
    return digitsOnly(u.mobilePlain);
  }
  const raw = u.mobile ?? u.mobile_number ?? u.mobileNumber ?? "";
  // Skip masked values like ****3210
  if (String(raw).includes("*")) return "";
  return digitsOnly(raw);
};

const traderBrokerageFeeEnabled = (t) => {
  const v = t?.brokerageFee ?? t?.brokerage_fee;
  if (v === true || v === 1 || v === "true" || v === "1") return true;
  return false;
};

/** Backend default is true; treat missing as enabled. */
const traderWithdrawalEnabled = (t) => {
  const v = t?.withdrawalEnabled ?? t?.withdrawal_enabled;
  if (v === false || v === 0 || v === "false" || v === "0") return false;
  return true;
};

const mapTraderDisplayRow = (t, isBlockedList) => {
  const firstName = t.firstName ?? t.first_name ?? "";
  const lastName = t.lastName ?? t.last_name ?? "";
  const emailDisplay = t.emailId ?? t.email_masked ?? "—";
  const resolvedCountryCode = extractTraderCountryCode(t);
  const mobilePlain =
    t.mobilePlain ??
    (String(t.mobileNumber ?? t.mobile ?? "").includes("*")
      ? ""
      : digitsOnly(t.mobileNumber ?? t.mobile ?? ""));

  if (!isBlockedList) {
    const mobileDisplay =
      t.mobile_masked ??
      (t.mobileNumber
        ? `${(resolvedCountryCode || t.country_code || "").toString().trim()} ${t.mobileNumber}`.trim()
        : t.mobileNumber ?? "—");
    return {
      ...t,
      emailId: emailDisplay,
      emailPlain: t.email ?? t.emailId ?? t.email_masked ?? "—",
      mobilePlain,
      mobileNumber: mobileDisplay,
      uuid: t.uuid ?? t.uuid_masked ?? "—",
      name: `${firstName} ${lastName}`.trim() || "—",
      countryCode: resolvedCountryCode,
      countryLabel: getCountryLabelByCode(resolvedCountryCode),
      kycLabel: KYC_LABELS[t.kycVerified ?? t.kyc_verified] ?? "—",
      statusLabel:
        STATUS_LABELS[t.status] ??
        (t.status === 1 ? "Active" : t.status === 0 ? "Inactive" : String(t.status ?? "—")),
      createdLabel: formatDate(t.createdAt ?? t.signup_date),
      brokerageFeeEnabled: traderBrokerageFeeEnabled(t),
      withdrawalEnabledFlag: traderWithdrawalEnabled(t),
    };
  }

  const mobileDisplay =
    t.mobile_masked ??
    (t.mobileNumber
      ? `${(resolvedCountryCode || t.country_code || "").toString().trim()} ${t.mobileNumber}`.trim()
      : "—");
  const st = t.status;
  const statusLabel =
    typeof st === "string"
      ? st
      : STATUS_LABELS[st] ?? (st === 1 ? "Active" : st === 0 ? "Inactive" : String(st ?? "—"));
  return {
    ...t,
    emailId: emailDisplay,
    emailPlain: t.email ?? t.emailId ?? t.email_masked ?? "—",
    mobilePlain,
    mobileNumber: mobileDisplay,
    uuid: t.uuid ?? t.uuid_masked ?? "—",
    name: `${firstName} ${lastName}`.trim() || "—",
    countryCode: resolvedCountryCode,
    countryLabel: getCountryLabelByCode(resolvedCountryCode),
    kycLabel: KYC_LABELS[t.kycVerified ?? t.kyc_verified ?? t.kyc_status] ?? "—",
    statusLabel,
    createdLabel: formatDate(t.createdAt ?? t.signup_date ?? t.created_at),
    accountStatusDisplay: String(t.account_status ?? t.accountStatus ?? "").trim() || "—",
  };
};

const traderToExportRow = (row, isBlockedList) => {
  const empty = (v) => (v == null || v === "—" ? "" : v);
  const base = {
    Email: empty(row.emailId),
    Mobile: empty(row.mobileNumber),
    "Country Code": empty(row.countryCode),
    Country: empty(row.countryLabel),
    Name: empty(row.name),
    UUID: empty(row.uuid),
    KYC: empty(row.kycLabel),
    "Referral Code": row.referral_code ?? "",
    Created: empty(row.createdLabel),
  };
  if (isBlockedList) {
    return {
      ...base,
      "Account status": empty(row.accountStatusDisplay),
      "Trading profile": empty(row.statusLabel),
    };
  }
  return {
    ...base,
    Status: empty(row.statusLabel),
  };
};

const TradersListPage = ({ variant = "default" }) => {
  const navigate = useNavigate();
  const isBlockedList = variant === "blocked";
  const isUnverifiedList = variant === "unverified";
  const usesExtendedFields = isBlockedList || isUnverifiedList;

  const fetchUsersList = useCallback(
    (params) => {
      if (isBlockedList) return AdminService.getBlockedUsers(params);
      if (isUnverifiedList) return AdminService.getUnverifiedUsers(params);
      return AdminService.getUsers(params);
    },
    [isBlockedList, isUnverifiedList]
  );

  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [brokerageFeeUpdating, setBrokerageFeeUpdating] = useState(null);
  const [withdrawalUpdating, setWithdrawalUpdating] = useState(null);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [kycFilter, setKycFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const [walletModalUser, setWalletModalUser] = useState(null);
  const [dcModalCoins, setDcModalCoins] = useState([]);
  const [dcModalWalletTypes, setDcModalWalletTypes] = useState([]);
  const [dcResourcesLoading, setDcResourcesLoading] = useState(false);
  const [dcResourcesError, setDcResourcesError] = useState("");
  const [dcForm, setDcForm] = useState({
    coinId: "",
    type: "CREDIT",
    amount: "",
    account_type: "balance",
    wallet_type: "spot",
  });
  const [dcSubmitting, setDcSubmitting] = useState(false);
  const [dcMessage, setDcMessage] = useState({ type: "", text: "" });
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState({ type: "", text: "" });

  const [contactModalUser, setContactModalUser] = useState(null);
  const [contactForm, setContactForm] = useState({
    emailId: "",
    mobileNumber: "",
    country_code: "",
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactMessage, setContactMessage] = useState({ type: "", text: "" });
  const [transferSettingsUser, setTransferSettingsUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const walletTypeOptions = useMemo(() => {
    if (dcModalWalletTypes.length) return dcModalWalletTypes.map((x) => String(x));
    return FALLBACK_WALLET_TYPES;
  }, [dcModalWalletTypes]);

  const buildFilterParams = useCallback(
    (pageNum, limitNum, includeCountryInApi = false) => {
      const baseParams = {
        page: pageNum,
        limit: limitNum,
        ...(searchDebounced.trim() && { search: searchDebounced.trim() }),
        ...(includeCountryInApi &&
          countryFilter.trim() && { country_code: countryFilter.trim() }),
      };
      if (isBlockedList) {
        return {
          ...baseParams,
          account_status: "Blocked",
          ...(kycFilter !== "" && { kyc_status: kycFilter }),
        };
      }
      return {
        ...baseParams,
        ...(kycFilter !== "" && { kyc_status: kycFilter }),
        ...(statusFilter !== "" && { status: statusFilter }),
      };
    },
    [searchDebounced, kycFilter, statusFilter, countryFilter, isBlockedList]
  );

  const fetchAllForCountryFilter = useCallback(async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await fetchUsersList(buildFilterParams(currentPage, EXPORT_BATCH, true));

      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to load traders.");
      }

      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages ?? res?.pagination?.totalPages ?? 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all.filter((trader) => traderMatchesCountryFilter(trader, countryFilter));
  }, [buildFilterParams, countryFilter, fetchUsersList]);

  const fetchTraders = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      if (countryFilter.trim()) {
        const filtered = await fetchAllForCountryFilter();
        const total = filtered.length;
        const totalPages = Math.max(1, Math.ceil(total / pagination.limit));
        const safePage = Math.min(pagination.page, totalPages);
        const start = (safePage - 1) * pagination.limit;
        setTraders(filtered.slice(start, start + pagination.limit));
        setPagination((prev) => ({
          ...prev,
          page: safePage,
          total,
          totalPages,
        }));
        return;
      }

      const res = await fetchUsersList(
        buildFilterParams(pagination.page, pagination.limit, true)
      );

      if (res?.success) {
        setTraders(res.data || []);
        const pag = res.pagination || {};
        setPagination((prev) => ({
          ...prev,
          total: pag.total ?? prev.total,
          totalPages: pag.total_pages ?? pag.totalPages ?? prev.totalPages,
        }));
      } else {
        setError(res?.message || "Failed to load traders.");
        setTraders([]);
      }
    } catch (err) {
      setError(err?.message || "Failed to load traders.");
      setTraders([]);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.limit,
    buildFilterParams,
    countryFilter,
    fetchAllForCountryFilter,
    fetchUsersList,
  ]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [searchDebounced, kycFilter, statusFilter, countryFilter]);

  useEffect(() => {
    fetchTraders();
  }, [fetchTraders]);

  useEffect(() => {
    if (!walletModalUser) return undefined;
    let cancelled = false;
    (async () => {
      setDcResourcesLoading(true);
      setDcResourcesError("");
      setDcMessage({ type: "", text: "" });
      try {
        const [cRes, tRes] = await Promise.all([
          AdminService.getAdminGetCoinList(),
          AdminService.getAdminExchangeWalletTypes(),
        ]);
        if (cancelled) return;
        const rawCoins = Array.isArray(cRes?.data) ? cRes.data : [];
        const coins = rawCoins
          .map((c) => ({
            id: String(c._id ?? c.id ?? "").trim(),
            label: `${c.short_name || c.name || "Coin"}${
              c.name && String(c.short_name).toUpperCase() !== String(c.name).toUpperCase()
                ? ` — ${c.name}`
                : ""
            }`,
          }))
          .filter((c) => c.id);
        setDcModalCoins(coins);

        let types = [];
        if (tRes?.success && Array.isArray(tRes.data)) {
          types = tRes.data.map((x) => String(x ?? "").trim()).filter(Boolean);
        }
        setDcModalWalletTypes(types);

        const wtOpts = types.length ? types : FALLBACK_WALLET_TYPES;
        setDcForm((f) => {
          const wt = wtOpts.includes(f.wallet_type) ? f.wallet_type : wtOpts[0];
          const firstCoin = coins[0]?.id ?? "";
          const coinId =
            f.coinId && coins.some((c) => c.id === f.coinId) ? f.coinId : firstCoin;
          return { ...f, wallet_type: wt, coinId };
        });
      } catch {
        if (!cancelled) {
          setDcResourcesError("Could not load coins or wallet types.");
          setDcModalCoins([]);
          setDcModalWalletTypes([]);
        }
      } finally {
        if (!cancelled) setDcResourcesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [walletModalUser]);

  const handleResetFilters = () => {
    setSearch("");
    setSearchDebounced("");
    setKycFilter("");
    setStatusFilter("");
    setCountryFilter("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      limit: Number(e.target.value) || 20,
      page: 1,
    }));
  };

  const fetchAllFilteredTraders = async () => {
    if (countryFilter.trim()) {
      return fetchAllForCountryFilter();
    }

    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await fetchUsersList(buildFilterParams(currentPage, EXPORT_BATCH, true));

      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }

      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages ?? res?.pagination?.totalPages ?? 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    setExportMessage({ type: "", text: "" });
    try {
      const allTraders = await fetchAllFilteredTraders();
      if (!allTraders.length) {
        setExportMessage({
          type: "error",
          text: "No traders match your filters to export.",
        });
        return;
      }

      const exportData = allTraders
        .map((t) => mapTraderDisplayRow(t, usesExtendedFields))
        .map((row) => traderToExportRow(row, usesExtendedFields));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      const sheetTitle = isBlockedList
        ? EXCEL_SHEET_BLOCKED
        : isUnverifiedList
          ? EXCEL_SHEET_UNVERIFIED
          : EXCEL_SHEET_TRADERS;
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);

      const filePrefix = isBlockedList
        ? "blocked_traders"
        : isUnverifiedList
          ? "unverified_traders"
          : "traders_list";
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `${filePrefix}_${dateStamp}.xlsx`);

      setExportMessage({
        type: "success",
        text: `Exported ${allTraders.length} trader(s) to Excel.`,
      });
      setTimeout(() => setExportMessage({ type: "", text: "" }), 4000);
    } catch (e) {
      console.error(e);
      setExportMessage({
        type: "error",
        text: e?.message || "Failed to export Excel file.",
      });
    } finally {
      setExportLoading(false);
    }
  };

  /** Default `/dashboard/tradelist` mapping stays unchanged; blocked/unverified APIs use extended fields below. */
  const rows = traders.map((t) => mapTraderDisplayRow(t, usesExtendedFields));

  const columns = useMemo(() => {
    if (usesExtendedFields) {
      return [
        { key: "emailId", label: "Email" },
        { key: "mobileNumber", label: "Mobile" },
        { key: "countryLabel", label: "Country" },
        { key: "name", label: "Name" },
        { key: "uuid", label: "UUID" },
        { key: "accountStatusDisplay", label: "Account status" },
        { key: "statusLabel", label: "Trading profile" },
        { key: "kycLabel", label: "KYC" },
        { key: "referral_code", label: "Referral Code" },
        { key: "createdLabel", label: "Created" },
        { key: "wallet", label: "Wallet" },
        { key: "internalTransfer", label: "Transfers" },
        { key: "actions", label: "Actions" },
      ];
    }
    return [
      { key: "emailId", label: "Email" },
      { key: "mobileNumber", label: "Mobile" },
      { key: "countryLabel", label: "Country" },
      { key: "name", label: "Name" },
      { key: "uuid", label: "UUID" },
      { key: "statusLabel", label: "Status" },
      { key: "brokerageFee", label: "Brokerage fee" },
      { key: "withdrawal", label: "Withdrawal" },
      { key: "kycLabel", label: "KYC" },
      { key: "referral_code", label: "Referral Code" },
      { key: "createdLabel", label: "Created" },
      { key: "wallet", label: "Wallet" },
      { key: "internalTransfer", label: "Transfers" },
      { key: "actions", label: "Actions" },
    ];
  }, [usesExtendedFields]);

  const handleAccountUnblock = async (trader) => {
    const userIdRaw = trader._id ?? trader.id;
    const userId = userIdRaw != null ? String(userIdRaw) : "";
    if (!userId || statusUpdating) return;
    setStatusUpdating(userId);
    setError("");
    try {
      const res = await AdminService.updateUserAccountBlockStatus(userId, "Active");
      if (res?.success) {
        await fetchTraders();
      } else {
        setError(res?.message || "Could not unblock account.");
      }
    } catch {
      setError("Could not unblock account.");
    } finally {
      setStatusUpdating(null);
    }
  };

  const handleBlockToggle = async (trader) => {
    if (isBlockedList) return;
    if (!trader._id || statusUpdating) return;
    const newStatus = trader.status === 1 || trader.status === "Active" ? "Inactive" : "Active";
    setStatusUpdating(trader._id);
    const res = await AdminService.updateUserStatus(trader._id, newStatus);
    setStatusUpdating(null);
    if (res?.success) {
      setTraders((prev) =>
        prev.map((t) =>
          t._id === trader._id
            ? { ...t, status: newStatus === "Active" ? 1 : 0 }
            : t
        )
      );
    }
  };

  const openDeleteModal = (trader) => {
    setDeleteError("");
    setDeleteTarget(trader);
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setDeleteTarget(null);
    setDeleteError("");
  };

  const handleConfirmDelete = async () => {
    const userIdRaw = deleteTarget?._id ?? deleteTarget?.id;
    const userId = userIdRaw != null ? String(userIdRaw) : "";
    if (!userId || deleteSubmitting) return;
    setDeleteSubmitting(true);
    setDeleteError("");
    try {
      const res = await AdminService.deleteUserAccount(userId);
      if (res?.success) {
        setDeleteTarget(null);
        toast.success(res.message || "User account deleted successfully");
        await fetchTraders();
      } else {
        setDeleteError(res?.message || "Failed to delete account.");
      }
    } catch (err) {
      setDeleteError(err?.message || "Failed to delete account.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleBrokerageFeeToggle = async (trader) => {
    if (isBlockedList) return;
    const userIdRaw = trader._id ?? trader.id;
    const userId = userIdRaw != null ? String(userIdRaw) : "";
    if (!userId || brokerageFeeUpdating) return;

    const currentEnabled = traderBrokerageFeeEnabled(trader);
    const nextEnabled = !currentEnabled;

    setBrokerageFeeUpdating(userId);
    setError("");
    try {
      const res = await AdminService.updateUserBrokerageFee(userId, nextEnabled);
      if (res?.success) {
        const updatedFee =
          typeof res.data?.brokerageFee === "boolean" ? res.data.brokerageFee : nextEnabled;
        setTraders((prev) =>
          prev.map((t) => {
            const id = String(t._id ?? t.id ?? "");
            if (id !== userId) return t;
            return { ...t, brokerageFee: updatedFee };
          })
        );
      } else {
        setError(res?.message || "Failed to update brokerage fee.");
      }
    } catch {
      setError("Failed to update brokerage fee.");
    } finally {
      setBrokerageFeeUpdating(null);
    }
  };

  const handleWithdrawalToggle = async (trader) => {
    if (isBlockedList) return;
    const userIdRaw = trader._id ?? trader.id;
    const userId = userIdRaw != null ? String(userIdRaw) : "";
    if (!userId || withdrawalUpdating) return;

    const currentEnabled = traderWithdrawalEnabled(trader);
    const nextEnabled = !currentEnabled;

    setWithdrawalUpdating(userId);
    setError("");
    try {
      const res = await AdminService.updateUserWithdrawalStatus(userId, nextEnabled);
      if (res?.success) {
        const updated =
          typeof res.data?.withdrawalEnabled === "boolean"
            ? res.data.withdrawalEnabled
            : nextEnabled;
        setTraders((prev) =>
          prev.map((t) => {
            const id = String(t._id ?? t.id ?? "");
            if (id !== userId) return t;
            return { ...t, withdrawalEnabled: updated };
          })
        );
      } else {
        setError(res?.message || "Failed to update withdrawal status.");
      }
    } catch {
      setError("Failed to update withdrawal status.");
    } finally {
      setWithdrawalUpdating(null);
    }
  };

  const openContactModal = (row) => {
    if (!row?._id && !row?.id) return;
    const email = traderEmailForModal(row);
    setContactModalUser(row);
    setContactForm({
      emailId: email === "—" ? "" : email,
      mobileNumber: traderMobileForModal(row),
      country_code: normalizeCountryCode(row.countryCode || row.country_code || "") || "+91",
    });
    setContactMessage({ type: "", text: "" });
  };

  const closeContactModal = () => {
    if (contactSubmitting) return;
    setContactModalUser(null);
    setContactMessage({ type: "", text: "" });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactModalUser || contactSubmitting) return;

    const userId = String(contactModalUser._id ?? contactModalUser.id ?? "");
    if (!userId) {
      setContactMessage({ type: "error", text: "User ID missing." });
      return;
    }

    const emailId = contactForm.emailId.trim();
    const mobileNumber = digitsOnly(contactForm.mobileNumber);
    const country_code = normalizeCountryCode(contactForm.country_code);

    if (!emailId && !mobileNumber) {
      setContactMessage({ type: "error", text: "Provide at least email or mobile number." });
      return;
    }
    if (emailId && !isValidEmailFormat(emailId)) {
      setContactMessage({ type: "error", text: "Enter a valid email address." });
      return;
    }
    if (emailId && !isAllowedEmailDomain(emailId)) {
      setContactMessage({
        type: "error",
        text: `Please use a ${ALLOWED_EMAIL_PROVIDERS_LABEL} email (same as registration).`,
      });
      return;
    }
    if (mobileNumber && (mobileNumber.length < 6 || mobileNumber.length > 15)) {
      setContactMessage({ type: "error", text: "Mobile number must be 6–15 digits." });
      return;
    }

    const body = { userId };
    if (emailId) body.emailId = emailId;
    if (mobileNumber) {
      body.mobileNumber = mobileNumber;
      if (country_code) body.country_code = country_code;
    }

    setContactSubmitting(true);
    setContactMessage({ type: "", text: "" });
    try {
      const res = await AdminService.updateUserContact(body);
      if (res?.success) {
        const data = res.data || {};
        setTraders((prev) =>
          prev.map((t) => {
            const id = String(t._id ?? t.id ?? "");
            if (id !== userId) return t;
            return {
              ...t,
              email: data.emailId ?? (emailId || t.email),
              emailId: data.emailId ?? (emailId || t.emailId),
              mobileNumber: data.mobileNumber ?? (mobileNumber || t.mobileNumber),
              mobile: data.mobileNumber ?? t.mobile,
              country_code: data.country_code ?? (country_code || t.country_code),
              firstName: data.firstName ?? t.firstName,
              lastName: data.lastName ?? t.lastName,
            };
          })
        );
        setContactMessage({
          type: "success",
          text: res.message || "User contact updated successfully.",
        });
        setTimeout(() => {
          setContactModalUser(null);
          setContactMessage({ type: "", text: "" });
        }, 900);
      } else {
        setContactMessage({
          type: "error",
          text: res?.message || "Failed to update contact.",
        });
      }
    } catch (err) {
      setContactMessage({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to update contact.",
      });
    } finally {
      setContactSubmitting(false);
    }
  };

  const openWalletModal = (row) => {
    if (!row._id) return;
    setWalletModalUser(row);
    setDcForm({
      coinId: "",
      type: "CREDIT",
      amount: "",
      account_type: "balance",
      wallet_type: "spot",
    });
    setDcModalCoins([]);
    setDcModalWalletTypes([]);
    setDcResourcesError("");
    setDcMessage({ type: "", text: "" });
  };

  const closeWalletModal = () => {
    if (dcSubmitting) return;
    setWalletModalUser(null);
    setDcMessage({ type: "", text: "" });
  };

  const openTransferSettingsModal = (row) => {
    const userId = row?._id ?? row?.id;
    if (!userId) return;
    setTransferSettingsUser(row);
  };

  const closeTransferSettingsModal = () => {
    setTransferSettingsUser(null);
  };

  const handleDebitCreditSubmit = async (e) => {
    e.preventDefault();
    if (!walletModalUser?._id) return;
    const amount = Number(dcForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setDcMessage({ type: "error", text: "Enter a valid amount greater than 0." });
      return;
    }
    if (!dcForm.coinId?.trim()) {
      setDcMessage({ type: "error", text: "Select a coin." });
      return;
    }
    setDcSubmitting(true);
    setDcMessage({ type: "", text: "" });
    try {
      const res = await AdminService.adminExchangeDebitCreditByAdmin({
        userId: String(walletModalUser._id),
        coinId: dcForm.coinId.trim(),
        type: dcForm.type,
        amount,
        account_type: dcForm.account_type,
        wallet_type: dcForm.wallet_type,
      });
      if (res?.success) {
        setDcMessage({ type: "success", text: res?.message || "Debit/Credit successful." });
        setTimeout(() => {
          setWalletModalUser(null);
          setDcMessage({ type: "", text: "" });
        }, 700);
      } else {
        setDcMessage({ type: "error", text: res?.message || "Request failed." });
      }
    } catch {
      setDcMessage({ type: "error", text: "Request failed." });
    } finally {
      setDcSubmitting(false);
    }
  };


  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>
              {isBlockedList
                ? "Blocked traders"
                : isUnverifiedList
                  ? "Unverified users"
                  : "Traders List"}
            </span>
          </p>
          <h1 className="dashboard-hero__title">
            {isBlockedList
              ? "Blocked trader list"
              : isUnverifiedList
                ? "Unverified users"
                : "Traders List"}
          </h1>
          <p className="dashboard-hero__subtitle">
            {isBlockedList
              ? "Users with a blocked account (login / access restricted). Unblock from here or open details."
              : isUnverifiedList
                ? "Users who have not completed verification. Search, filter, and download Excel."
                : "View and manage registered traders"}
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i
            className={
              isBlockedList
                ? "fas fa-user-lock"
                : isUnverifiedList
                  ? "fas fa-user-clock"
                  : "fas fa-user-tie"
            }
          />
        </div>
      </section>

      <div className="dashboard-body">
        <section
          className={
            isBlockedList
              ? "traders-section traders-section--blocked-users"
              : isUnverifiedList
                ? "traders-section traders-section--unverified-users"
                : "traders-section"
          }
        >
          <div className="traders-section__header">
            <div className="traders-section__title-wrap">
              <span className="traders-section__icon">
                <i
                  className={
                    isBlockedList
                      ? "fas fa-ban"
                      : isUnverifiedList
                        ? "fas fa-user-clock"
                        : "fas fa-users"
                  }
                />
              </span>
              <h2 className="traders-section__title">
                {isBlockedList
                  ? "Blocked traders"
                  : isUnverifiedList
                    ? "Unverified users"
                    : "Traders List"}
              </h2>
            </div>
            <div className="traders-section__stats traders-section__stats--actions">
              <span className="traders-stat">
                <strong>{pagination.total}</strong> Total
              </span>
              <button
                type="button"
                className="traders-export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered traders as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
            </div>
          </div>

          {exportMessage.text && (
            <p
              className={`traders-export-message traders-export-message--${exportMessage.type}`}
              role="status"
            >
              {exportMessage.text}
            </p>
          )}

          <form onSubmit={(e) => e.preventDefault()} className="traders-filters">
            <div className="traders-filters__row">
              <input
                type="text"
                placeholder="Search email, UUID, mobile, name, referral..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="traders-filter-search"
              />
              <select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="traders-filter-select traders-filter-select--country"
              >
                <option value="">All countries</option>
                {countriesList.map((country, index) => (
                  <option key={`${country.value}-${index}`} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
              <select
                value={kycFilter}
                onChange={(e) => setKycFilter(e.target.value)}
                className="traders-filter-select"
              >
                <option value="">All KYC</option>
                <option value="0">Not Verified</option>
                <option value="1">Pending</option>
                <option value="2">Verified (Complete KYC)</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="traders-filter-select"
                disabled={isBlockedList}
                style={isBlockedList ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <select
                value={pagination.limit}
                onChange={handleLimitChange}
                className="traders-filter-select"
              >
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
                <option value={500}>500 / page</option>
                <option value={1000}>1000 / page</option>
              </select>
              <button type="button" className="traders-filter-btn" onClick={handleResetFilters}>
                <i className="fas fa-undo" /> Reset
              </button>
            </div>
          </form>

          {error && (
            <div className="traders-error">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          <div className="traders-list-content">
            <div className="traders-table-wrap">
              <table className="traders-table">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && traders.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="traders-loading">
                        <i className="fas fa-spinner fa-spin" /> Loading traders…
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="traders-empty">
                        {isBlockedList
                          ? "No blocked traders found."
                          : isUnverifiedList
                            ? "No unverified users found."
                            : "No traders found."}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, i) => (
                      <tr key={row._id || i}>
                        {columns.map((col) => {
                          if (col.key === "wallet") {
                            return (
                              <td key={col.key} className="traders-table__wallet">
                                <button
                                  type="button"
                                  className="btn-icon btn-icon--wallet"
                                  onClick={() => openWalletModal(row)}
                                  title="Debit / credit user wallet"
                                  disabled={!row._id}
                                >
                                  <i className="fas fa-wallet" />
                                </button>
                              </td>
                            );
                          }
                          if (col.key === "internalTransfer") {
                            const uidStr = row._id ?? row.id;
                            return (
                              <td key={col.key} className="traders-table__transfers">
                                <button
                                  type="button"
                                  className="btn-icon btn-icon--transfer"
                                  onClick={() => openTransferSettingsModal(row)}
                                  title="Internal transfer settings"
                                  disabled={!uidStr}
                                  aria-label="Internal transfer settings"
                                >
                                  <i className="fas fa-exchange-alt" />
                                </button>
                              </td>
                            );
                          }
                          if (col.key === "accountStatusDisplay") {
                            const ac = String(row.accountStatusDisplay || "").trim().toLowerCase();
                            const pillClass =
                              ac === "blocked"
                                ? "badge badge--account-blocked"
                                : ac === "inactive"
                                  ? "badge badge--account-inactive"
                                  : "badge badge--account-active";
                            return (
                              <td key={col.key}>
                                <span className={pillClass}>{String(row.accountStatusDisplay || "—")}</span>
                              </td>
                            );
                          }
                          if (col.key === "brokerageFee") {
                            const uidStr = row._id ?? row.id;
                            const enabled = row.brokerageFeeEnabled;
                            const busy = brokerageFeeUpdating === uidStr;
                            return (
                              <td key={col.key} className="traders-table__brokerage">
                                <div className="traders-brokerage-cell">
                                  <label
                                    className="traders-toggle traders-toggle--sm"
                                    title={enabled ? "Brokerage fee enabled" : "Brokerage fee disabled"}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={enabled}
                                      onChange={() => handleBrokerageFeeToggle(row)}
                                      disabled={!uidStr || busy}
                                      aria-label={`Brokerage fee ${enabled ? "on" : "off"}`}
                                    />
                                    <span className="traders-toggle__slider" />
                                  </label>
                                  <span className={`traders-brokerage-status ${enabled ? "is-on" : "is-off"}`}>
                                    {busy ? (
                                      <i className="fas fa-spinner fa-spin" aria-hidden />
                                    ) : enabled ? (
                                      "On"
                                    ) : (
                                      "Off"
                                    )}
                                  </span>
                                </div>
                              </td>
                            );
                          }
                          if (col.key === "withdrawal") {
                            const uidStr = row._id != null || row.id != null ? String(row._id ?? row.id) : "";
                            const enabled = row.withdrawalEnabledFlag;
                            const busy = withdrawalUpdating === uidStr;
                            return (
                              <td key={col.key} className="traders-table__withdrawal">
                                <div className="traders-brokerage-cell">
                                  <label
                                    className="traders-toggle traders-toggle--sm"
                                    title={enabled ? "Withdrawals enabled" : "Withdrawals disabled"}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={enabled}
                                      onChange={() => handleWithdrawalToggle(row)}
                                      disabled={!uidStr || busy}
                                      aria-label={`Withdrawals ${enabled ? "on" : "off"}`}
                                    />
                                    <span className="traders-toggle__slider" />
                                  </label>
                                  <span className={`traders-brokerage-status ${enabled ? "is-on" : "is-off"}`}>
                                    {busy ? (
                                      <i className="fas fa-spinner fa-spin" aria-hidden />
                                    ) : enabled ? (
                                      "On"
                                    ) : (
                                      "Off"
                                    )}
                                  </span>
                                </div>
                              </td>
                            );
                          }
                          if (col.key === "actions") {
                            if (isBlockedList) {
                              const userIdRaw = row._id ?? row.id;
                              const uidStr = userIdRaw != null ? String(userIdRaw) : "";
                              const ac = String(
                                row.account_status ?? row.accountStatus ?? row.accountStatusDisplay ?? ""
                              )
                                .trim()
                                .toLowerCase();
                              const blocked = ac === "blocked";
                              const busy = statusUpdating === uidStr;
                              return (
                                <td key={col.key} className="traders-table__actions">
                                  <button
                                    type="button"
                                    className="btn-icon btn-icon--view"
                                    onClick={() => navigate(`/dashboard/trader/${row._id}`, { state: { trader: row } })}
                                    title="View details"
                                  >
                                    <i className="fas fa-eye" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-icon btn-icon--edit"
                                    onClick={() => openContactModal(row)}
                                    disabled={!uidStr}
                                    title="Edit email / mobile"
                                    aria-label="Edit email or mobile"
                                  >
                                    <i className="fas fa-user-edit" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-icon btn-icon--unblock"
                                    onClick={() => handleAccountUnblock(row)}
                                    disabled={!uidStr || !blocked || busy}
                                    title={blocked ? "Unblock account" : "Account not blocked"}
                                    aria-label="Unblock account"
                                  >
                                    {busy ? (
                                      <i className="fas fa-spinner fa-spin" aria-hidden />
                                    ) : (
                                      <i className="fas fa-unlock-alt" aria-hidden />
                                    )}
                                  </button>
                                </td>
                              );
                            }
                            return (
                              <td key={col.key} className="traders-table__actions">
                                <button
                                  type="button"
                                  className="btn-icon btn-icon--view"
                                  onClick={() => navigate(`/dashboard/trader/${row._id}`, { state: { trader: row } })}
                                  title="View details"
                                >
                                  <i className="fas fa-eye" />
                                </button>
                                <button
                                  type="button"
                                  className="btn-icon btn-icon--edit"
                                  onClick={() => openContactModal(row)}
                                  disabled={!row._id}
                                  title="Edit email / mobile"
                                  aria-label="Edit email or mobile"
                                >
                                  <i className="fas fa-user-edit" />
                                </button>
                                <button
                                  type="button"
                                  className="btn-icon btn-icon--commission"
                                  onClick={() =>
                                    navigate(`/dashboard/trader/${row._id}/referral-network`, {
                                      state: { trader: row },
                                    })
                                  }
                                  title="All commission details"
                                  aria-label="All commission details"
                                >
                                  <i className="fas fa-sitemap" />
                                </button>
                                <button
                                  type="button"
                                  className={`btn-icon ${row.status === 1 || row.status === "Active" ? "btn-icon--unblock" : "btn-icon--block"}`}
                                  onClick={() => handleBlockToggle(row)}
                                  disabled={statusUpdating === row._id}
                                  title={row.status === 1 || row.status === "Active" ? "Block" : "Unblock"}
                                >
                                  {statusUpdating === row._id ? (
                                    <i className="fas fa-spinner fa-spin" />
                                  ) : (
                                    <i
                                      className={
                                        row.status === 1 || row.status === "Active"
                                          ? "fas fa-lock"
                                          : "fas fa-unlock"
                                      }
                                    />
                                  )}
                                </button>
                                {variant === "default" && (
                                  <button
                                    type="button"
                                    className="btn-icon btn-icon--delete"
                                    onClick={() => openDeleteModal(row)}
                                    disabled={!row._id && !row.id}
                                    title="Delete account"
                                    aria-label="Delete account"
                                  >
                                    <i className="fas fa-trash" />
                                  </button>
                                )}
                              </td>
                            );
                          }
                          if (col.key === "statusLabel") {
                            return (
                              <td key={col.key}>
                                <span
                                  className={`badge badge--${
                                    row.status === 1 || row.status === "Active" ? "active" : "inactive"
                                  }`}
                                >
                                  {row.statusLabel}
                                </span>
                              </td>
                            );
                          }
                          if (col.key === "uuid") {
                            return (
                              <td key={col.key}>
                                <button
                                  type="button"
                                  className="traders-table__uuid-link"
                                  onClick={() => navigate(`/dashboard/trader/${row._id}`, { state: { trader: row } })}
                                  title="View user details"
                                >
                                  {row.uuid}
                                </button>
                              </td>
                            );
                          }
                          return (
                            <td key={col.key}>{row[col.key] ?? "—"}</td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && !loading && (
              <div className="traders-pagination">
                <span className="traders-pagination__info">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <div className="traders-pagination__buttons">
                  <button
                    type="button"
                    className="traders-pagination__btn"
                    disabled={pagination.page <= 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    <i className="fas fa-chevron-left" /> Prev
                  </button>
                  <button
                    type="button"
                    className="traders-pagination__btn"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next <i className="fas fa-chevron-right" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {walletModalUser && (
        <div className="modal-overlay" onClick={closeWalletModal} role="presentation">
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="trader-dc-modal-title">
            <div className="modal-header">
              <h2 id="trader-dc-modal-title" className="modal-title">
                Debit / credit wallet
              </h2>
              <button type="button" className="modal-close" onClick={closeWalletModal} aria-label="Close" disabled={dcSubmitting}>
                <i className="fas fa-times" />
              </button>
            </div>
            <form className="modal-body" onSubmit={handleDebitCreditSubmit}>
              <div className="dc-modal-user-head">
                <strong className="dc-modal-user-name">{walletModalUser.name}</strong>
              </div>
              <div className="modal-field dc-modal-user-field">
                <label>Email</label>
                <div className="dc-modal-readonly" title={traderEmailForModal(walletModalUser)}>
                  {traderEmailForModal(walletModalUser)}
                </div>
              </div>
              <div className="modal-field dc-modal-user-field">
                <label>UUID</label>
                <div className="dc-modal-readonly dc-modal-uuid">
                  {walletModalUser.uuid ?? walletModalUser.uuid_masked ?? "—"}
                </div>
              </div>
              {dcResourcesError && (
                <p style={{ color: "#b91c1c", fontSize: "0.85rem", marginBottom: "0.75rem" }}>{dcResourcesError}</p>
              )}
              {dcMessage.text && (
                <p
                  style={{
                    marginBottom: "0.75rem",
                    fontSize: "0.875rem",
                    color: dcMessage.type === "success" ? "#15803d" : "#b91c1c",
                  }}
                >
                  {dcMessage.text}
                </p>
              )}
              <div className="modal-field">
                <label htmlFor="dc-coin">Coin</label>
                <select
                  id="dc-coin"
                  value={dcForm.coinId}
                  onChange={(e) => setDcForm((f) => ({ ...f, coinId: e.target.value }))}
                  required
                  disabled={dcResourcesLoading || dcSubmitting || dcModalCoins.length === 0}
                >
                  {dcModalCoins.length === 0 ? (
                    <option value="">{dcResourcesLoading ? "Loading coins…" : "No coins"}</option>
                  ) : (
                    dcModalCoins.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="modal-field">
                <label htmlFor="dc-type">Credit / Debit</label>
                <select
                  id="dc-type"
                  value={dcForm.type}
                  onChange={(e) => setDcForm((f) => ({ ...f, type: e.target.value }))}
                  disabled={dcSubmitting}
                >
                  <option value="CREDIT">CREDIT</option>
                  <option value="DEBIT">DEBIT</option>
                </select>
              </div>
              <div className="modal-field">
                <label htmlFor="dc-wallet-type">Wallet type</label>
                <select
                  id="dc-wallet-type"
                  value={dcForm.wallet_type}
                  onChange={(e) => setDcForm((f) => ({ ...f, wallet_type: e.target.value }))}
                  disabled={dcSubmitting || dcResourcesLoading}
                >
                  {walletTypeOptions.map((t) => (
                    <option key={t} value={t}>
                      {formatWalletType(t)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-field">
                <label htmlFor="dc-account-type">Account type</label>
                <select
                  id="dc-account-type"
                  value={dcForm.account_type}
                  onChange={(e) => setDcForm((f) => ({ ...f, account_type: e.target.value }))}
                  disabled={dcSubmitting}
                >
                  <option value="balance">balance</option>
                  <option value="locked_balance">locked_balance</option>
                </select>
              </div>
              <div className="modal-field">
                <label htmlFor="dc-amount">Amount</label>
                <input
                  id="dc-amount"
                  type="number"
                  step="any"
                  min="0"
                  placeholder="0"
                  value={dcForm.amount}
                  onChange={(e) => setDcForm((f) => ({ ...f, amount: e.target.value }))}
                  required
                  disabled={dcSubmitting}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeWalletModal} disabled={dcSubmitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={dcSubmitting || dcResourcesLoading}>
                  {dcSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Submitting…
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {contactModalUser && (
        <div className="modal-overlay" onClick={closeContactModal} role="presentation">
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="trader-contact-modal-title"
          >
            <div className="modal-header">
              <h2 id="trader-contact-modal-title" className="modal-title">
                Edit contact
              </h2>
              <button
                type="button"
                className="modal-close"
                onClick={closeContactModal}
                aria-label="Close"
                disabled={contactSubmitting}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <form className="modal-body" onSubmit={handleContactSubmit}>
              <div className="dc-modal-user-head">
                <strong className="dc-modal-user-name">{contactModalUser.name}</strong>
              </div>
              <div className="modal-field dc-modal-user-field">
                <label>UUID</label>
                <div className="dc-modal-readonly dc-modal-uuid">
                  {contactModalUser.uuid ?? "—"}
                </div>
              </div>
              {contactMessage.text && (
                <p className={`modal-message modal-message--${contactMessage.type}`}>
                  {contactMessage.text}
                </p>
              )}
              <p className="traders-contact-hint">
                Update email and/or mobile. At least one field is required. Email must be{" "}
                {ALLOWED_EMAIL_PROVIDERS_LABEL} (same as registration).
              </p>
              <div className="modal-field">
                <label htmlFor="contact-email">Email</label>
                <input
                  id="contact-email"
                  type="email"
                  value={contactForm.emailId}
                  onChange={(e) =>
                    setContactForm((f) => ({ ...f, emailId: e.target.value }))
                  }
                  placeholder="user@example.com"
                  disabled={contactSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="modal-field">
                <label htmlFor="contact-country">Country code</label>
                <select
                  id="contact-country"
                  value={contactForm.country_code}
                  onChange={(e) =>
                    setContactForm((f) => ({ ...f, country_code: e.target.value }))
                  }
                  disabled={contactSubmitting}
                >
                  <option value="">Select country code</option>
                  {countriesList.map((country, index) => (
                    <option key={`${country.value}-${index}`} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-field">
                <label htmlFor="contact-mobile">Mobile number</label>
                <input
                  id="contact-mobile"
                  type="text"
                  inputMode="numeric"
                  value={contactForm.mobileNumber}
                  onChange={(e) =>
                    setContactForm((f) => ({
                      ...f,
                      mobileNumber: digitsOnly(e.target.value).slice(0, 15),
                    }))
                  }
                  placeholder="9876543210"
                  disabled={contactSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeContactModal}
                  disabled={contactSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={contactSubmitting}>
                  {contactSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Saving…
                    </>
                  ) : (
                    "Save contact"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {transferSettingsUser && (
        <div className="modal-overlay" onClick={closeTransferSettingsModal} role="presentation">
          <div
            className="modal modal--transfer-settings"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="trader-transfer-settings-title"
          >
            <div className="modal-header">
              <h2 id="trader-transfer-settings-title" className="modal-title">
                Internal transfer settings
              </h2>
              <button
                type="button"
                className="modal-close"
                onClick={closeTransferSettingsModal}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <div className="dc-modal-user-head">
                <strong className="dc-modal-user-name">{transferSettingsUser.name}</strong>
              </div>
              <div className="modal-field dc-modal-user-field">
                <label>Email</label>
                <div className="dc-modal-readonly" title={traderEmailForModal(transferSettingsUser)}>
                  {traderEmailForModal(transferSettingsUser)}
                </div>
              </div>
              <div className="modal-field dc-modal-user-field">
                <label>User ID</label>
                <div className="dc-modal-readonly dc-modal-uuid">
                  {String(transferSettingsUser._id ?? transferSettingsUser.id ?? "—")}
                </div>
              </div>
              <InternalTransferSettingsPanel
                userId={String(transferSettingsUser._id ?? transferSettingsUser.id ?? "")}
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeTransferSettingsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={closeDeleteModal} role="presentation">
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="trader-delete-modal-title"
          >
            <div className="modal-header">
              <h2 id="trader-delete-modal-title" className="modal-title">
                Delete account
              </h2>
              <button
                type="button"
                className="modal-close"
                onClick={closeDeleteModal}
                aria-label="Close"
                disabled={deleteSubmitting}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <p className="traders-delete-copy">
                Delete <strong>{deleteTarget.name || "this user"}</strong>
                {deleteTarget.uuid ? ` (UUID ${deleteTarget.uuid})` : ""}? This removes the account and cannot be undone.
              </p>
              {deleteError ? <p className="modal-message modal-message--error">{deleteError}</p> : null}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeDeleteModal}
                  disabled={deleteSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={deleteSubmitting}
                >
                  {deleteSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Deleting…
                    </>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </main>
  );
};

export default TradersListPage;
